import { useState } from "react";
import { AlertCircle, ArrowRight, Check, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";
import { Hint, SideCard, Steps, SuccessBox, inputCls, type PanelProps } from "./channel-wizard-kit";

/* Instagram Direct: вход в аккаунт по логину и паролю, затем код 2FA.
   После входа диалоги и ответы на сторис идут в общую ленту. */

const DEMO_LOGIN = "hub.market";
const DEMO_PASSWORD = "hub-demo-2026";
const DEMO_CODE = "123456";

export default function InstagramLogin({ onDone }: PanelProps) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [signed, setSigned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  const canLogin = login.trim().length >= 2 && password.length >= 6 && !busy;
  const handle = login.trim().replace(/^@/, "");

  const signIn = () => {
    if (!canLogin) return;
    setBusy(true);
    setError("");
    setTimeout(() => {
      setBusy(false);
      setSigned(true);
    }, 800);
  };

  const verify = () => {
    setBusy(true);
    setError("");
    setTimeout(() => {
      setBusy(false);
      if (code.trim() !== DEMO_CODE) {
        setError(t("Код не совпал. В демо-режиме подходит 123456 — или запросите новый код в приложении."));
        return;
      }
      onDone(`@${handle}`, [
        { label: t("Тип"), value: t("Instagram · аккаунт") },
        { label: t("Аккаунт"), value: `@${handle}` },
        { label: t("Способ входа"), value: t("логин · код 2FA") },
        { label: t("Сторис"), value: t("ответы включены") },
      ]);
    }, 700);
  };

  return (
    <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 p-4 md:p-5">
        <Steps
          active={signed ? 2 : 1}
          items={[
            t("Введите логин и пароль аккаунта Instagram"),
            t("Подтвердите вход кодом из приложения"),
            t("Готово — сообщения и сторис идут во «Входящие»"),
          ]}
        />

        {!signed ? (
          <div className="mt-4 grid gap-2.5">
            <div>
              <label
                htmlFor="ig-login"
                className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {t("Логин или e-mail")}
              </label>
              <input
                id="ig-login"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="hub.market"
                autoComplete="off"
                className={cn(inputCls, "mt-1.5")}
              />
            </div>
            <div>
              <label
                htmlFor="ig-password"
                className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {t("Пароль")}
              </label>
              <input
                id="ig-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                className={cn(inputCls, "mt-1.5")}
              />
            </div>

            <button
              type="button"
              onClick={signIn}
              disabled={!canLogin}
              className="mt-1 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
              {busy ? t("Входим…") : t("Войти в Instagram")}
              <ArrowRight className="size-3.5" aria-hidden />
            </button>

            <button
              type="button"
              onClick={() => {
                setLogin(DEMO_LOGIN);
                setPassword(DEMO_PASSWORD);
              }}
              className="inline-flex w-fit cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-primary transition hover:brightness-110"
            >
              <Sparkles className="size-3" aria-hidden />
              {t("Заполнить демо-данными")}
            </button>
          </div>
        ) : (
          <div className="mt-4">
            <p className="flex items-center gap-1.5 text-[11.5px] font-semibold text-emerald-600">
              <Check className="size-3.5" strokeWidth={3} aria-hidden />
              {t("Пароль верный — аккаунт @{handle}", { handle })}
            </p>

            <label
              htmlFor="ig-code"
              className="mt-3 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
            >
              {t("Код из приложения")}
            </label>
            <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
              <input
                id="ig-code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setError("");
                }}
                placeholder="— — — — — —"
                inputMode="numeric"
                autoComplete="off"
                className={cn(inputCls, "font-mono text-center tracking-[0.5em]")}
              />
              <button
                type="button"
                onClick={verify}
                disabled={code.length < 6 || busy}
                className="inline-flex h-10 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
                {busy ? t("Проверяем…") : t("Подтвердить")}
              </button>
            </div>

            {error ? (
              <p className="mt-2 flex items-start gap-1.5 rounded-btn border border-red-400/40 bg-red-500/10 px-2.5 py-2 text-[11.5px] leading-relaxed text-red-600">
                <AlertCircle className="mt-0.5 size-3.5 flex-none" aria-hidden />
                {error}
              </p>
            ) : null}

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setCode(DEMO_CODE);
                  setError("");
                }}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-primary transition hover:brightness-110"
              >
                <Sparkles className="size-3" aria-hidden />
                {t("Вставить демо-код")}
              </button>
              <span className="text-[11px] text-muted-foreground">·</span>
              <button
                type="button"
                onClick={() => {
                  setSigned(false);
                  setCode("");
                  setError("");
                }}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] font-medium text-muted-foreground transition hover:text-foreground"
              >
                {t("Другой аккаунт")}
              </button>
            </div>

            {code === DEMO_CODE && !error ? <SuccessBox title={t("Код верный — привязываем аккаунт")} /> : null}
          </div>
        )}

        <Hint icon={<ShieldCheck className="size-3.5" />}>
          {t("Пароль не сохраняется: после входа доступ держится на токене, а не на логине. Видит его только администратор.")}
        </Hint>
      </div>

      <div className="flex min-w-0 flex-col gap-3 border-t border-border bg-background p-4 lg:border-s lg:border-t-0 md:p-5">
        <SideCard title={t("Аккаунт для привязки")}>
          <div className="mt-2.5 flex items-center gap-2.5">
            <span
              className="grid size-10 flex-none place-items-center rounded-full bg-primary/10 text-lg leading-none text-primary"
              aria-hidden
            >
              <i className="ri-instagram-line" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-[12.5px] font-semibold">{handle ? `@${handle}` : t("аккаунт не указан")}</p>
              <p className="truncate text-[11px] text-muted-foreground">
                {signed ? t("пароль подтверждён") : t("ждём вход")}
              </p>
            </div>
          </div>
          <div className="mt-3 flex flex-col gap-1.5">
            <span className={cn("flex items-center gap-1.5 text-[11px]", signed ? "text-emerald-600" : "text-muted-foreground")}>
              <Check className={cn("size-3", signed ? "opacity-100" : "opacity-30")} strokeWidth={3} aria-hidden />
              {t("Логин и пароль приняты")}
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span
                className={cn("size-3 flex-none rounded-full border", code.length === 6 ? "border-primary bg-primary" : "border-border")}
                aria-hidden
              />
              {t("Код 2FA введён")}
            </span>
          </div>
        </SideCard>

        <SideCard title={t("Что будет после входа")}>
          <ul className="mt-2 flex flex-col gap-1.5">
            {[
              t("Личные сообщения → во «Входящие»"),
              t("Ответы на сторис и упоминания"),
              t("Имя и аватар клиента подтягиваются"),
            ].map((item) => (
              <li key={item} className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                <Check className="mt-0.5 size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </SideCard>
      </div>
    </div>
  );
}